/**
 * The composer while it records (Figma 05 · Voice, Chat / Composer ·
 * Recording): an accent hairline round the composer, the live square, the
 * level as accent bars, the time; Cancel, "Arabic or English", and Stop.
 * Voice is only a way to type: the recording is written down and dropped.
 */
import { useEffect, useRef } from "react";
import { X } from "lucide-react";

/** "0:07": whole seconds, as the recording counts them. */
function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function Bars({ values, height, className }: { values: number[]; height: number; className: string }) {
  return (
    <span className={className} style={{ blockSize: height }} aria-hidden>
      {values.map((v, i) => (
        <i key={i} style={{ blockSize: Math.max(2, Math.round(v * height)) }} />
      ))}
    </span>
  );
}

/** The composer's body while it records. */
export function VoiceRecordingBar({
  levels,
  elapsedMs,
  onCancel,
  onStop,
}: {
  levels: number[];
  elapsedMs: number;
  onCancel: () => void;
  onStop: () => void;
}) {
  const stopRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    stopRef.current?.focus({ preventScroll: true });
  }, []);
  // The newest sample on the right; quiet until there is sound.
  const shown = levels.length > 0 ? levels : [0];
  return (
    <div
      className="m7r-recording"
      role="group"
      aria-label="Recording a voice note"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }}
    >
      <div className="m7r-recording-listen">
        <span className="m7r-recording-live" aria-hidden />
        <Bars values={shown.map((v) => Math.max(0.2, v))} height={28} className="m7r-wave m7r-wave--live" />
        <span className="m7r-recording-time" role="timer" aria-label={`Recording, ${formatClock(elapsedMs / 1000)}`}>
          {formatClock(elapsedMs / 1000)}
        </span>
      </div>
      <div className="m7r-recording-controls">
        <button type="button" className="m7r-recording-cancel" onClick={onCancel}>
          <X aria-hidden strokeWidth={2} />
          Cancel
        </button>
        <span className="m7r-recording-hint">Arabic or English</span>
        <button
          ref={stopRef}
          type="button"
          className="m7r-recording-stop"
          aria-label="Stop and write it down"
          title="Stop"
          onClick={onStop}
        >
          <span aria-hidden />
        </button>
      </div>
    </div>
  );
}
